// types/content.ts
// New type definitions for chunked content with buffering support

/**
 * A section of content that has been chunked for efficient loading
 * Supports both inline content (legacy) and contentPath (lazy loading)
 */
export interface ContentSection {
  id: string;
  title: string;
  content?: string; // Chunk of ~500-1000 words (legacy/inline)
  contentPath?: string; // Path to .txt file for lazy loading (new)
  wordCount: number;
  startWordIndex: number; // Position in full content
  endWordIndex: number;
}

/**
 * Story metadata without content (for lazy loading)
 */
export interface StoryMetadata {
  id: string;
  wpm: number;
  title: string;
  wordCount: number;
  spoilerFreeSummary: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  sections: ContentSection[];
}

/**
 * Category metadata containing only story metadata (no content)
 */
export interface CategoryMetadata {
  stories: StoryMetadata[];
}

/**
 * Configuration for the content buffer system
 */
export interface BufferConfig {
  enabled: boolean;
  preloadNextSection: boolean;
  preloadWordCount: number; // Start preloading when N words remaining
  maxSectionsInMemory: number;
}

/**
 * Category item with chunked sections instead of monolithic content
 */
export interface CategoryItemChunked {
  id: string;
  title: string;
  sections: ContentSection[]; // Instead of single content string
  totalWordCount: number;
  categoryId: string;
  // Metadata
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  readingTimeMinutes: number;
  source?: string;
}

/**
 * Options for useRSVPReader with buffer support
 */
export interface UseRSVPReaderOptions {
  content?: string; // Legacy: single content string
  sections?: ContentSection[]; // New: chunked sections
  initialWpm: number;
  onComplete: (result: SessionResult) => void;
  onWpmChange: (wpm: number) => void;
  rampConfig: RampConfig;
  progressMode?: 'word' | 'time';
  autoStart?: boolean;
  allowSectionNavigation?: boolean;
  allowTimeSkip?: boolean;
  enableKeyboardShortcuts?: boolean;
  bufferConfig?: BufferConfig;
}

/**
 * Ramp configuration for WPM progression
 */
export interface RampConfig {
  enabled: boolean;
  startWpm: number;
  endWpm: number;
  durationSeconds: number;
}

/**
 * Session result after completing reading
 */
export interface SessionResult {
  wordsRead: number;
  durationSeconds: number;
  avgWpm: number;
  maxWpm: number;
  date: string;
}

/**
 * Return type for useContentBuffer hook
 */
export interface UseContentBufferReturn {
  currentSection: ContentSection | null;
  nextSection: ContentSection | null;
  currentSectionIndex: number;
  preloadProgress: number; // 0-100
  isPreloading: boolean;
  // Actions
  jumpToSection: (index: number) => void;
  setCurrentWordIndex: (index: number) => void;
}

/**
 * Options for useContentBuffer hook
 */
export interface UseContentBufferOptions {
  sections: ContentSection[];
  initialSectionIndex?: number;
  initialWordIndex?: number;
  bufferConfig: BufferConfig;
}

// Default buffer configuration
export const DEFAULT_BUFFER_CONFIG: BufferConfig = {
  enabled: true,
  preloadNextSection: true,
  preloadWordCount: 100, // Start preloading when 100 words remaining
  maxSectionsInMemory: 3, // Keep current, previous, and next sections
};

/**
 * A single chapter extracted from an uploaded book file
 */
export interface ProcessedBookChapter {
  id: string;          // Unique identifier, e.g. "chapter-1"
  title: string;       // Display title, e.g. "Chapter 1: The Beginning"
  content: string;     // The chapter's text content
  wordCount: number;   // Word count for this chapter
}

/**
 * Result of processing an uploaded book file (EPUB, PDF, TXT, etc.)
 * Replaces the old (content: string, filename: string) callback signature
 */
export interface ProcessedBookResult {
  title: string;
  author?: string;
  chapters: ProcessedBookChapter[];
  fullText: string;    // Concatenated chapter content for backward compatibility
  totalWords: number;
  source: 'epub' | 'pdf' | 'txt' | 'html' | 'md' | 'docx';
  warnings?: string[]; // Non-fatal issues (e.g. "No chapters detected")
  bookId?: string;     // NEW: Optional unique identifier for progress tracking (generated for imports)
}
